import { useEffect, useState } from 'react';
import {
  Building2,
  Handshake,
  Search,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  ShieldCheck,
  Zap,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useMesterplanApi } from '@mesterplan/web-hooks';
import { EmailUnverifiedNotice } from '@/components/partners/EmailUnverifiedNotice';
import { useNetUserContext } from '@/contexts/NetUserContext';
import { isEmailUnverifiedError } from '@/lib/emailVerification';
import { useCurrentDpaVersion } from '@/hooks/useDpa';
import { DPA_PDF_URL, TERMS_URL } from '@/lib/legalLinks';

interface CreateFirstCompanyScreenProps {
  /** Called with the new org id after a successful bootstrap. */
  onCreated: (orgId: string) => void;
}

const CVR_RE = /^\d{8}$/;

// A CVR is copied out of invoices and virk.dk in every shape: "DK12345678",
// "12 34 56 78", or both at once. The CVR is the trailing 8 digits.
function cleanCvr(raw: string): string {
  return raw.trim().replace(/^dk/i, '').replace(/\s/g, '');
}

const VALUE_PROPS = [
  { icon: Search, text: 'Automatisk CVR-opslag — vi udfylder resten' },
  { icon: ShieldCheck, text: 'Dine oplysninger gemmes sikkert' },
  { icon: Zap, text: 'Klar til at komme i gang på under et minut' },
];

export function CreateFirstCompanyScreen({ onCreated }: CreateFirstCompanyScreenProps) {
  const api = useMesterplanApi();
  const { user: netUser } = useNetUserContext();
  const [cvr, setCvr] = useState('');
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [lookupHint, setLookupHint] = useState<string | null>(null);
  const [lookupOk, setLookupOk] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [looking, setLooking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // A partner user (invited to collaborate on another company's work, no company of their
  // own) creates a partner-tier company instead of a full one. Null until the status call
  // says so; a failed status call leaves the ordinary flow in place.
  const [partner, setPartner] = useState<{ invitedByCompanyName: string | null } | null>(null);
  // Submitting before the status is known would create a Full company for a partner, and
  // after that the partner path is gone (they have a membership) — so hold the button.
  const [partnerStatusSettled, setPartnerStatusSettled] = useState(false);
  // The API will not tell an unconfirmed address whether it was invited as a partner, so the
  // user is asked to confirm it instead of silently getting the ordinary company flow.
  const [emailUnverified, setEmailUnverified] = useState(false);
  const [city, setCity] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  // The published DPA version, or null while none is published (the checkbox is then not shown
  // and company creation is exactly as before).
  const [dpaAccepted, setDpaAccepted] = useState(false);
  const dpa = useCurrentDpaVersion();
  const dpaVersion = dpa.state === 'ready' ? dpa.version : null;
  // While the version loads, or when it failed to load, the company must not be created: that
  // would skip the acceptance. Only a 404 (nothing published) means "no DPA".
  const dpaBlocking = dpa.state === 'loading' || dpa.state === 'error' || (dpaVersion !== null && !dpaAccepted);

  useEffect(() => {
    let cancelled = false;
    async function loadPartnerStatus() {
      try {
        const res = await api.companies.partnerConversionStatus();
        if (!cancelled && res.ok && res.data.eligible) {
          setPartner({ invitedByCompanyName: res.data.invitedByCompanyName });
        } else if (!cancelled && !res.ok && isEmailUnverifiedError(res.error)) {
          setEmailUnverified(true);
        }
      } catch {
        // Not a partner as far as we can tell — the ordinary flow still works.
      } finally {
        if (!cancelled) setPartnerStatusSettled(true);
      }
    }
    void loadPartnerStatus();
    return () => {
      cancelled = true;
    };
  }, [api]);

  const cleanedCvr = cleanCvr(cvr);
  const cvrValid = CVR_RE.test(cleanedCvr);

  function handleCvrChange(value: string) {
    setCvr(value);
    // Editing the CVR invalidates a previous lookup result.
    if (lookupOk) setLookupOk(false);
    if (lookupHint) setLookupHint(null);
  }

  async function handleLookup() {
    setLookupHint(null);
    setLookupOk(false);
    setError(null);
    if (!cvrValid) return;
    setLooking(true);
    try {
      const res = await api.cvr.lookup(cleanedCvr);
      if (res.ok) {
        setName(res.data.name);
        setAddress(res.data.address);
        setLookupOk(true);
      } else {
        setLookupHint('Kunne ikke hente oplysninger — udfyld manuelt.');
      }
    } catch {
      setLookupHint('Kunne ikke hente oplysninger — udfyld manuelt.');
    } finally {
      setLooking(false);
    }
  }

  async function handleSubmit() {
    setError(null);
    if (!cvrValid || !name.trim() || !address.trim()) {
      setError('Udfyld CVR-nummer, navn og adresse.');
      return;
    }
    setSubmitting(true);
    try {
      const company = {
        name: name.trim(),
        vat: cleanedCvr,
        address: address.trim(),
        ...(dpaVersion && dpaAccepted ? { dpaAcceptance: { version: dpaVersion } } : {}),
      };
      const res = partner
        ? await api.companies.convertPartner({
            ...company,
            city: city.trim() || null,
            phone: phone.trim() || null,
            email: email.trim() || null,
          })
        : await api.companies.bootstrap(company);
      if (res.ok) {
        onCreated(res.data);
      } else if (isEmailUnverifiedError(res.error)) {
        setEmailUnverified(true);
      } else if (res.status === 409) {
        setError('Der findes allerede en virksomhed med dette CVR.');
      } else if (res.status === 400 && partner) {
        // The partner endpoint says why in `detail` (e.g. the invitation was revoked);
        // a field-validation 400 carries none — the optional email is the likely culprit.
        setError(res.error?.detail ?? 'Tjek oplysningerne — fx email-adressen.');
      } else {
        setError('Noget gik galt. Prøv igen.');
      }
    } catch {
      setError('Noget gik galt. Prøv igen.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen w-full flex-col bg-white dark:bg-dark-card lg:flex-row">
      {/* ── Branded panel ────────────────────────────────────────────── */}
      <aside className="relative isolate flex flex-col justify-between overflow-hidden bg-gradient-to-br from-primary-700 via-primary to-secondary px-8 py-10 text-white lg:w-[44%] lg:px-12 lg:py-14">
        {/* Blueprint grid — a nod to the craftsman planning the work */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 -z-10 opacity-[0.18]"
          style={{
            backgroundImage:
              'linear-gradient(to right, rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.6) 1px, transparent 1px)',
            backgroundSize: '26px 26px',
            maskImage: 'radial-gradient(120% 70% at 75% 0%, black 35%, transparent 80%)',
            WebkitMaskImage: 'radial-gradient(120% 70% at 75% 0%, black 35%, transparent 80%)',
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-16 -top-20 -z-10 h-64 w-64 rounded-full bg-white/20 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-24 -left-10 -z-10 h-64 w-64 rounded-full bg-secondary/40 blur-3xl"
        />

        <div className="animate-in fade-in slide-in-from-left-2 duration-500">
          <div className="mb-7 flex h-14 w-14 items-center justify-center rounded-2xl border border-white/25 bg-white/15 shadow-lg backdrop-blur-sm">
            <Building2 className="h-7 w-7" strokeWidth={2} />
          </div>
          <p className="text-[13px] font-medium uppercase tracking-[0.16em] text-white/70">
            Velkommen til Mesterplan
          </p>
          <h1 className="mt-2 max-w-[15ch] text-3xl font-bold leading-[1.15] text-white lg:text-4xl">
            Opret din virksomhed
          </h1>
          <p className="mt-4 max-w-[34ch] text-[15px] leading-relaxed text-white/85">
            Indtast dit CVR-nummer, så henter vi resten. Du er i gang på under et minut.
          </p>
        </div>

        <ul className="mt-10 space-y-3.5 animate-in fade-in slide-in-from-left-2 duration-500 delay-150">
          {VALUE_PROPS.map(({ icon: Icon, text }) => (
            <li key={text} className="flex items-center gap-3 text-[14px] text-white/90">
              <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-white/15">
                <Icon className="h-4 w-4" />
              </span>
              {text}
            </li>
          ))}
        </ul>
      </aside>

      {/* ── Form panel ───────────────────────────────────────────────── */}
      <main className="flex flex-1 items-center justify-center px-6 py-12 sm:px-10">
        <div className="w-full max-w-md animate-in fade-in slide-in-from-bottom-2 duration-500 delay-100">
          <h2 className="text-xl font-semibold text-dark dark:text-dark-text">
            Virksomhedsoplysninger
          </h2>
          <p className="mt-1 text-[14px] text-muted dark:text-dark-muted">
            Start med dit CVR-nummer, eller udfyld felterne manuelt.
          </p>

          <div className="mt-8 space-y-5">
            {emailUnverified && <EmailUnverifiedNotice email={netUser?.email} />}

            {partner && (
              <div className="flex items-start gap-2.5 rounded-lg border border-primary/30 bg-primary/5 px-3.5 py-2.5 dark:bg-primary/10">
                <Handshake className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                <p className="text-[13px] leading-snug text-dark dark:text-dark-text">
                  <span className="font-semibold">
                    Du er inviteret som samarbejdspartner
                    {partner.invitedByCompanyName ? ` af ${partner.invitedByCompanyName}` : ''}.
                  </span>{' '}
                  Din virksomhed oprettes som partnervirksomhed.
                </p>
              </div>
            )}

            {/* CVR with inline lookup */}
            <div>
              <Label htmlFor="onboarding-cvr">CVR-nummer</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted dark:text-dark-muted" />
                  <Input
                    id="onboarding-cvr"
                    className="pl-9"
                    value={cvr}
                    onChange={(e) => handleCvrChange(e.target.value)}
                    placeholder="12345678"
                    inputMode="numeric"
                    maxLength={24}
                  />
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={!cvrValid || looking}
                  onClick={handleLookup}
                  className="gap-2"
                >
                  {looking ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Henter…
                    </>
                  ) : (
                    'Hent oplysninger'
                  )}
                </Button>
              </div>
              {lookupHint && (
                <p className="mt-1.5 flex items-center gap-1.5 text-[13px] text-warning">
                  <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                  {lookupHint}
                </p>
              )}
            </div>

            {/* Success confirmation — the delightful moment after a hit */}
            {lookupOk && (
              <div className="flex items-start gap-2.5 rounded-lg border border-success/30 bg-success-light px-3.5 py-2.5 dark:bg-success/10 animate-in fade-in slide-in-from-top-1 duration-300">
                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-success" />
                <p className="text-[13px] leading-snug text-dark dark:text-dark-text">
                  <span className="font-semibold">Virksomhed fundet.</span> Tjek
                  oplysningerne nedenfor og ret om nødvendigt.
                </p>
              </div>
            )}

            {/* Name + address — always editable, lookup or not */}
            <div>
              <Label htmlFor="onboarding-name">Virksomhedsnavn</Label>
              <Input
                id="onboarding-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="F.eks. Mester VVS ApS"
              />
            </div>

            <div>
              <Label htmlFor="onboarding-address">Adresse</Label>
              <Input
                id="onboarding-address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Vej, postnr. og by"
              />
            </div>

            {partner && (
              <>
                <div>
                  <Label htmlFor="onboarding-city">By (valgfri)</Label>
                  <Input
                    id="onboarding-city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="2650 Hvidovre"
                  />
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="onboarding-phone">Telefon (valgfri)</Label>
                    <Input
                      id="onboarding-phone"
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="onboarding-email">Email (valgfri)</Label>
                    <Input
                      id="onboarding-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                </div>
              </>
            )}

            {error && (
              <div className="flex items-start gap-2.5 rounded-lg border border-error/30 bg-error-light px-3.5 py-2.5 dark:bg-error/10 animate-in fade-in slide-in-from-top-1 duration-300">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-error" />
                <p className="text-[13px] leading-snug text-error">{error}</p>
              </div>
            )}

            {(dpa.state === 'ready' || dpa.state === 'error') && (
              <label className="flex cursor-pointer items-start gap-2.5 text-[14px] leading-snug text-muted dark:text-dark-muted">
                <input
                  type="checkbox"
                  checked={dpaAccepted}
                  disabled={dpa.state === 'error'}
                  onChange={(e) => setDpaAccepted(e.target.checked)}
                  className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-bordergray text-primary focus:ring-primary"
                />
                <span>
                  Jeg accepterer{' '}
                  <a href={TERMS_URL} target="_blank" rel="noreferrer" className="text-primary underline">
                    vilkårene
                  </a>{' '}
                  og{' '}
                  <a href={DPA_PDF_URL} target="_blank" rel="noreferrer" className="text-primary underline">
                    databehandleraftalen
                  </a>{' '}
                  på vegne af virksomheden.
                </span>
              </label>
            )}
            {dpa.state === 'error' && (
              <p className="flex items-center gap-1.5 text-[13px] text-warning">
                <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
                Vi kunne ikke hente databehandleraftalen.
                <button type="button" onClick={dpa.retry} className="font-medium text-primary underline">
                  Prøv igen
                </button>
              </p>
            )}

            <Button
              type="button"
              size="lg"
              disabled={submitting || !partnerStatusSettled || dpaBlocking}
              onClick={handleSubmit}
              className="group mt-2 w-full gap-2"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Opretter…
                </>
              ) : (
                <>
                  Opret virksomhed
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                </>
              )}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
